import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, effect, EventEmitter, Inject, Input, Output, Signal, signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { filter, map } from 'rxjs';
import { CustomizerModel } from 'src/business/services/client.generated';
import { PricePipe } from 'src/core-lib/pipes/price.pipe';
import { MediaModule } from 'src/layout/media/media.module';
import { MarketSegment } from 'src/utils/market-segment.util';
import { SerializerUtil } from 'src/utils/serializer.util';
import { UrlUtil } from 'src/utils/url.util';
import { Customizer, CustomizerDialogFilter, CustomizerDialogHead, CustomizerDialogView, CustomizerRequest, CustomizerResponse, CustomizerSelected, ProductModel } from '../../model';
import { CUSTOMIZER, CustomizerWorkerSubject } from '../../token';

@Component({
    selector: 'app-customizer-dialog',
    standalone: true,
    imports: [
        MediaModule,
        TranslateModule,
        PricePipe,
        CommonModule,
    ],
    templateUrl: './customizer-dialog.component.html',
    styleUrl: './customizer-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomizerDialogComponent {
    @Input()
    id: string;
    @Input()
    selected: CustomizerSelected;
    vatTranslationKey = MarketSegment.isPrivateCustomer() ? 'CUSTOMIZER.VIEW_INCL_VAT' : 'CUSTOMIZER.VIEW_EX_VAT';
    loading$: Signal<boolean>;
    response$: Signal<CustomizerResponse>;
    dialogFilters$: Signal<CustomizerDialogFilter[]>;
    dialogView$: Signal<CustomizerDialogView>;
    selected$: WritableSignal<FormData>;
    dialogHead$: Signal<CustomizerDialogHead>;
    filterData$: WritableSignal<FormData>;
    @Output()
    response = new EventEmitter<CustomizerResponse>();
    @Output()
    request = new EventEmitter<CustomizerRequest>();
    @Output()
    customizerChanged = new EventEmitter<CustomizerModel>();
    @Input()
    removable: boolean
    @Input()
    index: number

    constructor(
        protected _detector: ChangeDetectorRef,
        protected _location: Location,
        protected _router: Router,
        @Inject(CUSTOMIZER)
        protected _customizer: CustomizerWorkerSubject<CustomizerRequest, CustomizerResponse>,
    ) {
        // console.debug(this.id)
        this.loading$ = toSignal(this._customizer.loading$.pipe(map(a => a[this.id])));
        this.response$ = toSignal(this._customizer.response$.pipe(filter(a => a.id == this.id)));
        this.selected$ = signal<FormData>(null);
        this.filterData$ = signal<FormData>(null);
        this.dialogView$ = computed(() => {
            let response = this.response$();
            let filterData: FormData = this.filterData$();
            return Customizer.mapDialogView(response);
        })

        effect(() => {
            let response = this.response$();
            this.response.emit(response);
            this._detector.detectChanges();
            if (response == null || response.customizer == null)
                return;

            this.updateUrl(response);
            if (!response.inView) {
                if (this.dialog.open) {
                    this.dialog.close();
                }
            } else {
                if (!this.dialog.open) {
                    this.dialog.showModal();
                }
                this.scrollIntoView(response);
            }
            this._detector.markForCheck();
        })
    }

    get dialog(): HTMLDialogElement {
        return globalThis.document.getElementById('customizer-dialog-' + this.id) as HTMLDialogElement;
    }

    optionById(id: string): HTMLElement {
        return globalThis.document.getElementById('customizer-dialog-' + this.id + '--' + id)?.parentNode as HTMLElement;
    }

    dialogClose(event: Event) {
        this._detector.markForCheck();
        return false;
    }

    openView(event: Partial<CustomizerRequest>) {
        let resp = this.response$();
        let request = {
            customizer: resp.customizer,
            id: this.id,
            path: event.path,
            selected: this.selected
        } as CustomizerRequest;
        this.request.emit(request)
        this._customizer.postMessage(request);
    }

    selectOption(event: Partial<CustomizerRequest>) {
        let resp = this.response$();
        let request = {
            customizer: resp.customizer,
            id: this.id,
            path: event.path,
            select: event.select,
            selected: this.selected
        } as CustomizerRequest;
        this.request.emit(request)
        this._customizer.postMessage(request);
    }

    private scrollIntoView(state: CustomizerResponse) {
        setTimeout(() => {
            const elm = this.optionById(state.inView.selected);
            if (elm == null)
                return;
            elm.scrollIntoView();
            this._detector.markForCheck();
        }, 100);
    }

    updateUrl(resp: CustomizerResponse, _delete: boolean = false) {
        if (!resp?.mainLayout && !resp?.accessoryLayout?.isSelcted)
            return
        let prefix = resp?.accessoryLayout?.isSelcted ? UrlUtil.ACCESSORY_VARIATION_PREFIX : UrlUtil.VARIATION_PREFIX;
        let __index = resp?.accessoryLayout?.isSelcted ? `-${this.index}` : '';
        let _url = new URL(window.location.href);
        [resp.selected.main, ...resp.selected.addons].map((prod) => {
            let [pid, pvar] = prod.split(',');
            if (_delete)
                _url.searchParams.delete((prefix + pid + __index), pvar);
            else
                _url.searchParams.set((prefix + pid + __index), pvar);
        });
        let url = SerializerUtil.serialize(_url);
        this._location.replaceState(url);
        this._router.getCurrentNavigation();
        this._detector.markForCheck();
    }

    changed(productId, select) {
        let response = this.response$();
        let request = {
            customizer: response.customizer,
            id: this.id,
            path: [productId, ...select.split(';').filter((_, i) => i < response.inView.type)],
            select: `${productId},${select}`,
            selected: this.selected
        } as CustomizerRequest;
        this.request.emit(request)
        this._customizer.postMessage(request);
    }

    filterChanged() {
        // update formdata
    }



    goTo(path: string[] | null) {
        if (path == null) {
            if (this.dialog.open)
                this.dialog.close()
            return
        }
        let response = this.response$();
        let request = {
            customizer: response.customizer,
            id: this.id,
            path,
            selected: this.selected
        } as CustomizerRequest;
        this.request.emit(request)
        this._customizer.postMessage(request);
    }

}
